import { useEffect, useState } from 'react';
import { hubAPI } from '@/lib/api';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Plus, Pencil, Trash2, Layers, GitBranch, Sparkles, ArrowLeft, Save, X, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PipelineEditorDialog } from './PipelineEditorDialog';
import { ThemeIcon, THEME_ICON_OPTIONS } from '@/lib/themeIcons';

// Palette proposée par défaut pour un nouveau thème
const THEME_COLORS = [
    '#6366F1', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
    '#10B981', '#06B6D4', '#EF4444', '#64748B', '#F97316',
];

// Quelques emojis d'icônes suggérés
const ICON_SUGGESTIONS = ['💡', '🏗️', '⚡', '🌱', '🚀', '📊', '🎯', '🔧', '📝', '🏆', '💼', '🎨'];

// Pipeline par défaut proposé à la création d'un thème
const DEFAULT_PIPELINE = [
    { id: 'nouveau', name: 'Nouveau', color: '#94A3B8', order: 1, max_days: 7 },
    { id: 'en_cours', name: 'En cours', color: '#3B82F6', order: 2, max_days: 14 },
    { id: 'validation', name: 'Validation', color: '#F59E0B', order: 3, max_days: 7 },
    { id: 'termine', name: 'Terminé', color: '#10B981', order: 4, max_days: null },
];

/**
 * Gestionnaire complet des thèmes (admin).
 * - Liste tous les thèmes existants + boutons éditer / supprimer / éditer pipeline
 * - Formulaire de création/édition (nom, couleur, icône, description)
 * - Édition du pipeline via `PipelineEditorDialog`
 */
export function ThemeManagerDialog({ open, onOpenChange, onChange, spaceId }) {
    const [themes, setThemes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState('list'); // list | form
    const [editing, setEditing] = useState(null); // thème en cours d'édition (null = création)
    const [form, setForm] = useState(defaultForm());
    const [saving, setSaving] = useState(false);
    const [pipelineDialogFor, setPipelineDialogFor] = useState(null); // thème dont on édite le pipeline
    const [confirmDelete, setConfirmDelete] = useState(null);

    function defaultForm() {
        return { name: '', color: THEME_COLORS[0], icon: ICON_SUGGESTIONS[0], description: '' };
    }

    const load = async () => {
        setLoading(true);
        try {
            // Scope l'affichage aux thèmes de cet espace (+ legacy globaux) si fourni.
            const r = await hubAPI.listThemes(spaceId ? { space_id: spaceId } : undefined);
            setThemes(r.data || []);
        } catch {
            toast.error('Impossible de charger les thèmes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            setMode('list');
            setEditing(null);
            load();
        }
    }, [open]);

    const startCreate = () => {
        setEditing(null);
        setForm(defaultForm());
        setMode('form');
    };

    const startEdit = (theme) => {
        setEditing(theme);
        setForm({
            name: theme.name || '',
            color: theme.color || THEME_COLORS[0],
            icon: theme.icon || ICON_SUGGESTIONS[0],
            description: theme.description || '',
        });
        setMode('form');
    };

    const submit = async () => {
        if (!form.name.trim()) {
            toast.error('Le nom du thème est requis');
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await hubAPI.updateTheme(editing.id, form);
                toast.success('Thème mis à jour');
            } else {
                // À la création on impose un pipeline par défaut modifiable ensuite
                // ET on rattache le thème à l'espace courant (obligatoire dans la nouvelle UX).
                await hubAPI.createTheme({ ...form, pipeline: DEFAULT_PIPELINE, space_id: spaceId || null });
                toast.success('Thème créé. Vous pouvez maintenant personnaliser son pipeline.');
            }
            await load();
            setMode('list');
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\u2019enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async (theme) => {
        try {
            await hubAPI.deleteTheme(theme.id);
            toast.success(`Thème « ${theme.name} » supprimé`);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Suppression impossible');
        } finally {
            setConfirmDelete(null);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="theme-manager-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-indigo-600" />
                            {mode === 'list' ? 'Gérer les thèmes du Hub' : editing ? 'Modifier le thème' : 'Nouveau thème'}
                        </DialogTitle>
                        <DialogDescription>
                            {mode === 'list'
                                ? 'Les thèmes définissent les catégories de projets et leur pipeline (colonnes du kanban). Ils sont partagés entre tous les espaces de travail.'
                                : editing
                                    ? 'Modifiez le nom, la couleur, l\u2019icône ou la description. Le pipeline se modifie séparément via son bouton dédié.'
                                    : 'Créez un nouveau thème. Un pipeline par défaut sera fourni — vous pourrez le personnaliser ensuite.'}
                        </DialogDescription>
                    </DialogHeader>

                    {mode === 'list' ? (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="text-xs text-slate-500">
                                    {themes.length} thème{themes.length > 1 ? 's' : ''} existant{themes.length > 1 ? 's' : ''}
                                </div>
                                <Button size="sm" onClick={startCreate} data-testid="btn-create-theme" className="gap-1.5">
                                    <Plus className="w-4 h-4" /> Créer un thème
                                </Button>
                            </div>
                            {loading ? (
                                <div className="text-center text-sm text-slate-400 py-6">Chargement…</div>
                            ) : themes.length === 0 ? (
                                <div className="text-center text-sm text-slate-400 py-8">
                                    <Sparkles className="w-6 h-6 inline mb-2 text-slate-300" />
                                    <div>Aucun thème pour le moment.</div>
                                    <div className="text-xs mt-1">Créez votre premier thème pour commencer à organiser vos projets.</div>
                                </div>
                            ) : (
                                <div className="space-y-2" data-testid="theme-list">
                                    {themes.map((t) => (
                                        <ThemeRow
                                            key={t.id}
                                            theme={t}
                                            onEdit={() => startEdit(t)}
                                            onDelete={() => setConfirmDelete(t)}
                                            onEditPipeline={() => setPipelineDialogFor(t)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <ThemeForm form={form} setForm={setForm} theme={editing} onEditPipeline={editing ? () => setPipelineDialogFor(editing) : null} />
                    )}

                    <DialogFooter>
                        {mode === 'form' ? (
                            <>
                                <Button variant="outline" onClick={() => setMode('list')} data-testid="btn-back-list" className="gap-1">
                                    <ArrowLeft className="w-4 h-4" /> Retour
                                </Button>
                                <Button onClick={submit} disabled={saving} data-testid="btn-save-theme" className="gap-1">
                                    <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : (editing ? 'Enregistrer' : 'Créer le thème')}
                                </Button>
                            </>
                        ) : (
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Édition du pipeline via le dialogue existant */}
            {pipelineDialogFor && (
                <PipelineEditorDialog
                    open={!!pipelineDialogFor}
                    onOpenChange={(o) => { if (!o) setPipelineDialogFor(null); }}
                    theme={pipelineDialogFor}
                    onSaved={() => {
                        load();
                        onChange?.();
                        setPipelineDialogFor(null);
                    }}
                />
            )}

            {/* Confirmation de suppression */}
            <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
                <DialogContent className="max-w-md" data-testid="confirm-delete-theme">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Supprimer le thème ?
                        </DialogTitle>
                        <DialogDescription>
                            Le thème <span className="font-semibold text-slate-900">« {confirmDelete?.name} »</span> sera supprimé.
                            Si des projets l\u2019utilisent, la suppression sera bloquée — vous devrez d\u2019abord les migrer vers un autre thème.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
                        <Button variant="destructive" onClick={() => doDelete(confirmDelete)} data-testid="btn-confirm-delete-theme" className="gap-1">
                            <Trash2 className="w-4 h-4" /> Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function ThemeRow({ theme, onEdit, onDelete, onEditPipeline }) {
    const stageCount = (theme.pipeline || []).length;
    return (
        <div className="rounded-lg border border-slate-200 p-3 hover:border-slate-300 transition-colors" data-testid={`theme-row-${theme.id}`}>
            <div className="flex items-start gap-3">
                <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${theme.color}20`, color: theme.color }}
                >
                    <ThemeIcon icon={theme.icon} className="w-5 h-5" color={theme.color} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="font-semibold text-slate-900">{theme.name}</div>
                        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">
                            {stageCount} étape{stageCount > 1 ? 's' : ''}
                        </Badge>
                    </div>
                    {theme.description && (
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{theme.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                        {(theme.pipeline || []).slice(0, 6).map((s) => (
                            <span
                                key={s.id}
                                className="text-[10px] px-1.5 py-0.5 rounded-full"
                                style={{ backgroundColor: `${s.color || '#94A3B8'}20`, color: s.color || '#475569' }}
                            >
                                {s.name}
                            </span>
                        ))}
                        {stageCount > 6 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                                +{stageCount - 6}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={onEditPipeline} className="h-8 gap-1 text-xs" data-testid={`btn-edit-pipeline-${theme.id}`}>
                        <GitBranch className="w-3.5 h-3.5" /> Pipeline
                    </Button>
                    <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={onEdit} className="h-7 w-7" data-testid={`btn-edit-theme-${theme.id}`}>
                            <Pencil className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={onDelete} className="h-7 w-7 hover:bg-red-50" data-testid={`btn-delete-theme-${theme.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ThemeForm({ form, setForm, theme, onEditPipeline }) {
    return (
        <div className="space-y-4">
            {/* Aperçu */}
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 flex items-center gap-3">
                <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${form.color}20`, color: form.color }}
                >
                    <ThemeIcon icon={form.icon} className="w-6 h-6" color={form.color} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900">{form.name || 'Aperçu du thème'}</div>
                    <div className="text-xs text-slate-500 line-clamp-1">{form.description || 'La description apparaîtra ici.'}</div>
                </div>
            </div>

            <div>
                <Label>Nom *</Label>
                <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ex : Travaux d’optimisation, Levée de fonds, Recrutement…"
                    data-testid="theme-name"
                    autoFocus
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label>Couleur</Label>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                        {THEME_COLORS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setForm({ ...form, color: c })}
                                className={cn(
                                    'w-7 h-7 rounded-full border-2 transition-transform',
                                    form.color === c ? 'border-slate-900 scale-110' : 'border-white hover:scale-105',
                                )}
                                style={{ backgroundColor: c }}
                                data-testid={`theme-color-${c.replace('#', '')}`}
                                aria-label={`Couleur ${c}`}
                            />
                        ))}
                    </div>
                </div>
                <div>
                    <Label>Icône</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {ICON_SUGGESTIONS.map((emoji) => (
                            <button
                                key={emoji}
                                type="button"
                                onClick={() => setForm({ ...form, icon: emoji })}
                                className={cn(
                                    'w-8 h-8 rounded-lg border text-base transition-all',
                                    form.icon === emoji
                                        ? 'border-indigo-500 bg-indigo-50 scale-105'
                                        : 'border-slate-200 bg-white hover:border-slate-300',
                                )}
                                data-testid={`theme-icon-${emoji}`}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div>
                <Label>Description</Label>
                <Textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="À quoi sert ce thème ? Quels types de projets s'y rangent ?"
                    data-testid="theme-description"
                />
            </div>

            {theme && onEditPipeline && (
                <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 flex items-center justify-between">
                    <div className="text-xs text-slate-700 flex items-center gap-2">
                        <GitBranch className="w-4 h-4 text-indigo-600" />
                        Le pipeline (les étapes) se modifie séparément
                    </div>
                    <Button size="sm" variant="outline" onClick={onEditPipeline} data-testid="btn-goto-pipeline" className="gap-1">
                        <GitBranch className="w-3.5 h-3.5" /> Éditer le pipeline
                    </Button>
                </div>
            )}
        </div>
    );
}
